import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_POLLS = 20;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const pid = params.get("pid");
  const [payment, setPayment] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!pid) return;
    let stop = false;
    const check = async () => {
      try {
        const { data } = await api.get(`/payments/${pid}/status`);
        if (stop) return;
        setPayment(data.payment);
        setConversationId(data.conversation_id);
        if (data.payment.status === "pending" && polls.current < MAX_POLLS) {
          polls.current += 1;
          setTimeout(check, 4000);
        }
      } catch {
        if (!stop && polls.current < MAX_POLLS) {
          polls.current += 1;
          setTimeout(check, 4000);
        }
      }
    };
    check();
    return () => { stop = true; };
  }, [pid]);

  if (!pid) {
    return (
      <div className="container-mc py-24 text-center" data-testid="payment-return-invalid">
        <p className="text-muted-foreground">Référence de paiement manquante.</p>
        <Link to="/dashboard" className="btn-primary mt-6 inline-flex">Tableau de bord</Link>
      </div>
    );
  }

  const status = payment?.status;

  return (
    <div className="container-mc py-20" data-testid="payment-return-page">
      <div className="max-w-lg mx-auto card-mc p-10 text-center">
        <div className="gn-strip h-1.5 -mt-10 -mx-10 mb-10" />
        {(!payment || status === "pending") && (
          <div data-testid="payment-pending">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-[hsl(var(--primary))]" />
            <h1 className="font-display font-black text-3xl tracking-tighter mt-6">Vérification du paiement…</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Nous confirmons votre paiement auprès de FedaPay. Cela peut prendre quelques instants.
              Si vous avez validé le paiement sur votre téléphone, cette page se mettra à jour automatiquement.
            </p>
          </div>
        )}
        {status === "succeeded" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-[hsl(var(--accent))]" />
            <h1 className="font-display font-black text-3xl tracking-tighter mt-6">Paiement confirmé</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Référence <span className="font-mono font-bold text-foreground">{payment.reference}</span>
              {payment.type === "unlock_fee" && " — la mise en relation est activée, vous pouvez maintenant échanger avec l'artiste."}
              {payment.type === "commission" && " — la commission est réglée, le contrat est clôturé. Merci !"}
              {payment.type === "ticket_purchase" && " — vos billets sont activés et envoyés par email. Retrouvez-les dans « Mes billets »."}
              {payment.type === "vote" && ` — vos ${payment.quantity || ""} vote(s) sont comptabilisés. Merci pour votre soutien !`}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              {payment.type === "unlock_fee" && conversationId && (
                <button className="btn-primary" data-testid="go-to-conversation-btn" onClick={() => navigate(`/messages/${conversationId}`)}>
                  Ouvrir la conversation
                </button>
              )}
              {payment.type === "vote" && payment.poll_id && (
                <button className="btn-primary" data-testid="go-to-poll-btn" onClick={() => navigate(`/votes/${payment.poll_id}`)}>
                  Voir le classement
                </button>
              )}
              {payment.type === "ticket_purchase" && (
                <button className="btn-primary" data-testid="go-to-tickets-btn" onClick={() => navigate("/tickets")}>
                  Mes billets
                </button>
              )}
              <Link to="/dashboard" className="btn-outline" data-testid="go-to-dashboard-btn">Tableau de bord</Link>
            </div>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="w-14 h-14 mx-auto text-destructive" />
            <h1 className="font-display font-black text-3xl tracking-tighter mt-6">Paiement non abouti</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Le paiement a été refusé ou annulé. Aucun montant n'a été débité. Vous pouvez réessayer depuis votre tableau de bord.
            </p>
            <Link to="/dashboard" className="btn-primary mt-8 inline-flex" data-testid="retry-dashboard-btn">Retour au tableau de bord</Link>
          </div>
        )}
      </div>
    </div>
  );
}
